import { refreshTokenApi } from "@/api/services/main/auth";
import { removeToken } from "@/utils/token";
import Cookies from "js-cookie";

type TokenChangeCallback = (token: string | null) => void;

class AuthTokenManager {
  private static instance: AuthTokenManager;
  private listeners: Set<TokenChangeCallback> = new Set();
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  private constructor() {}

  static getInstance(): AuthTokenManager {
    if (!AuthTokenManager.instance) {
      AuthTokenManager.instance = new AuthTokenManager();
    }
    return AuthTokenManager.instance;
  }

  getToken(): string | null {
    return Cookies.get("match_token") || null;
  }

  onTokenChange(callback: TokenChangeCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyTokenChange(token: string | null): void {
    this.listeners.forEach((cb) => cb(token));
  }

  async refreshToken(): Promise<string | null> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<string | null> {
    const email = Cookies.get("email");
    const refreshToken = Cookies.get("refresh_token");
    const ownerId = Cookies.get("ownerId");

    if (!email || !refreshToken || !ownerId) {
      this.clearTokens();
      return null;
    }

    try {
      await refreshTokenApi({ refreshToken, email, ownerId });
      const newToken = this.getToken();
      this.notifyTokenChange(newToken);
      return newToken;
    } catch {
      this.clearTokens();
      return null;
    }
  }

  clearTokens(): void {
    removeToken();
    Cookies.remove("userId");
    Cookies.remove("rsoId");
    Cookies.remove("email");
    Cookies.remove("ownerId");
    Cookies.remove("isUser");
    Cookies.remove("x-auth-source");
    this.notifyTokenChange(null);
  }

  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }
}

export const authTokenManager = AuthTokenManager.getInstance();
